/**
 * Modal de FILTROS da lista de orçamentos: status, período e faixa de valor.
 *
 * ─── DUAS DECISÕES QUE NÃO SÃO DE LAYOUT ────────────────────────────────────
 *
 * **(a) O modal edita um RASCUNHO, não o filtro ativo.** Escrever direto no
 * estado da tela faria a lista se reorganizar atrás do modal a cada tecla
 * digitada no campo de valor — inclusive nos estados intermediários ("R$ 1,00"
 * a caminho de "R$ 1.000,00"), que recortariam quase tudo. O rascunho nasce do
 * filtro ativo toda vez que o modal abre, e só o "Aplicar" o promove; fechar
 * sem aplicar não muda nada, que é o que um botão "Cancelar" promete.
 *
 * **(b) Dinheiro entra por MÁSCARA, nunca por digitação livre (#157).** Não
 * existe parser que resolva a ambiguidade do ponto em pt-BR: "1.500" é mil e
 * quinhentos para quem escreve e é 1,5 para quem vem do teclado numérico. A
 * máscara recebe só os dígitos, empurra da direita em centavos e formata — aí
 * não há o que interpretar. Campo VAZIO vira `null`, e isso não é detalhe:
 * `parseBRLInput('')` é `0`, e `0` é um piso legítimo (#61) — colapsar os dois
 * transformaria "sem mínimo" em "no mínimo zero".
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button3D } from '@/components/ui/button-3d';
import { FieldLabel } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { FILTER_CHIP, FILTER_CHIP_OFF, FILTER_CHIP_ON } from '@/components/filters/filterChips';
import { maskBRLInput, numberToMaskedBRL, parseBRLInput } from '@/lib/currencyInput';
import { PERIOD_PRESET_LABELS, type ReportPeriodPreset } from '@/lib/reportPeriod';
import {
  QUOTE_PERIOD_PRESETS,
  type QuoteStatusFilter,
  type QuoteValueRange,
} from '@/lib/quoteListFilters';

/**
 * Mesma pílula chata dos botões de status da lista — uma fonte só.
 *
 * Ela MOROU aqui até `/clients` e `/catalog` adotarem o mesmo desenho; hoje
 * vive em `@/components/filters/filterChips` (uma constante de estilo dentro de
 * um diálogo obriga a terceira tela a importar de um componente que ela não
 * usa — ou, o que sempre acontece, a copiar as classes). O re-export permanece
 * porque a `QuotesListPage` importa daqui.
 */
export { FILTER_CHIP, FILTER_CHIP_ON, FILTER_CHIP_OFF } from '@/components/filters/filterChips';

export interface QuoteStatusChoice {
  value: QuoteStatusFilter;
  label: string;
}

interface QuoteFiltersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  statusOptions: readonly QuoteStatusChoice[];
  status: QuoteStatusFilter;
  period: ReportPeriodPreset | null;
  value: QuoteValueRange;
  onApply: (next: {
    status: QuoteStatusFilter;
    period: ReportPeriodPreset | null;
    value: QuoteValueRange;
  }) => void;
}

export function QuoteFiltersDialog({
  open,
  onOpenChange,
  statusOptions,
  status,
  period,
  value,
  onApply,
}: QuoteFiltersDialogProps) {
  const [draftStatus, setDraftStatus] = useState<QuoteStatusFilter>(status);
  const [draftPeriod, setDraftPeriod] = useState<ReportPeriodPreset | null>(period);
  const [minText, setMinText] = useState('');
  const [maxText, setMaxText] = useState('');

  // O rascunho é semeado na ABERTURA. Semear a cada render desfaria o que o
  // usuário está escolhendo; semear só na montagem deixaria o modal exibindo a
  // escolha da vez anterior quando ele fosse reaberto.
  useEffect(() => {
    if (!open) return;
    setDraftStatus(status);
    setDraftPeriod(period);
    setMinText(value.min === null ? '' : numberToMaskedBRL(value.min));
    setMaxText(value.max === null ? '' : numberToMaskedBRL(value.max));
  }, [open, status, period, value.min, value.max]);

  /** Vazio = SEM limite. Ver (b) no cabeçalho. */
  const toLimit = (text: string): number | null => (text.trim() ? parseBRLInput(text) : null);

  const aplicar = () => {
    onApply({
      status: draftStatus,
      period: draftPeriod,
      value: { min: toLimit(minText), max: toLimit(maxText) },
    });
    onOpenChange(false);
  };

  const limpar = () => {
    setDraftStatus('all');
    setDraftPeriod(null);
    setMinText('');
    setMaxText('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Filtros</DialogTitle>
          <DialogDescription>
            Combine status, período e faixa de valor para achar um documento.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <FieldLabel>Status</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setDraftStatus(option.value)}
                  className={cn(
                    FILTER_CHIP,
                    draftStatus === option.value ? FILTER_CHIP_ON : FILTER_CHIP_OFF,
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <FieldLabel>Período</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {/* "Qualquer data" é uma opção EXPLÍCITA, e não a ausência de
                  seleção: sem ela, quem escolheu um período não teria como
                  voltar atrás sem limpar o modal inteiro. */}
              <button
                type="button"
                onClick={() => setDraftPeriod(null)}
                className={cn(
                  FILTER_CHIP,
                  draftPeriod === null ? FILTER_CHIP_ON : FILTER_CHIP_OFF,
                )}
              >
                Qualquer data
              </button>
              {QUOTE_PERIOD_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setDraftPeriod(preset)}
                  className={cn(
                    FILTER_CHIP,
                    draftPeriod === preset ? FILTER_CHIP_ON : FILTER_CHIP_OFF,
                  )}
                >
                  {PERIOD_PRESET_LABELS[preset]}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <FieldLabel>Faixa de valor</FieldLabel>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <FieldLabel htmlFor="filtro-valor-min">De</FieldLabel>
                <Input
                  id="filtro-valor-min"
                  inputMode="numeric"
                  placeholder="R$ 0,00"
                  value={minText}
                  onChange={(e) => setMinText(maskBRLInput(e.target.value))}
                />
              </div>
              <div className="space-y-1">
                <FieldLabel htmlFor="filtro-valor-max">Até</FieldLabel>
                <Input
                  id="filtro-valor-max"
                  inputMode="numeric"
                  placeholder="Sem limite"
                  value={maxText}
                  onChange={(e) => setMaxText(maskBRLInput(e.target.value))}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-row gap-2 sm:justify-between">
          <Button3D color="gray" size="sm" className="flex-1" onClick={limpar}>
            Limpar
          </Button3D>
          <Button3D size="sm" className="flex-1" onClick={aplicar}>
            Aplicar
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
